import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { fileToCompressedDataUrl } from '../../utils/image';
import CalendarExceptions from '../../components/admin/CalendarExceptions';

const DAYS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export default function BusinessPage({ defaultTab }) {
  const { user, hasPlan } = useAuth();
  const [business, setBusiness] = useState(null);
  const [hours, setHours] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(''); // '' = horarios generales
  const [subTab, setSubTab] = useState('semanales');            // semanales | excepciones
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingHours, setSavingHours] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [tab, setTab] = useState(defaultTab || 'link');
  const [form, setForm] = useState({
    name: '', address: '', phone: '', email: '', negotiation_type: 'plaza', logo_url: ''
  });
  const [copied, setCopied] = useState(false);

  useEffect(() => { load(); }, []);

  const loadHours = async (branchId) => {
    const params = branchId ? { branch_id: branchId } : {};
    const hRes = await api.get(`/businesses/${user.business_id}/hours`, { params });
    const existing = hRes.data;
    const dayMap = [1, 2, 3, 4, 5, 6, 0];
    const toHHMM = v => {
      if (!v) return null;
      const s = v instanceof Date
        ? `${String(v.getHours()).padStart(2,'0')}:${String(v.getMinutes()).padStart(2,'0')}`
        : v.toString().match(/\d{2}:\d{2}/)?.[0] || null;
      return s;
    };
    const built = DAYS.map((_, i) => {
      const d = dayMap[i];
      const found = existing.find(h => h.dia_semana === d);
      return found
        ? {
            day_of_week: d,
            open_time:  toHHMM(found.hora_apertura) || '08:00',
            close_time: toHHMM(found.hora_cierre)   || '18:00',
            is_open:    !!(found.abierto),
            has_break:   !!(found.tiene_break),
            break_start: toHHMM(found.hora_break_inicio) || '13:00',
            break_end:   toHHMM(found.hora_break_fin)    || '14:00',
          }
        : { day_of_week: d, open_time: '08:00', close_time: '18:00', is_open: false,
            has_break: false, break_start: '13:00', break_end: '14:00' };
    });
    setHours(built);
  };

  const load = async () => {
    try {
      const res = await api.get(`/businesses/${user.business_id}`);
      const b = res.data;
      setBusiness(b);
      setForm({
        name: b.nombre || b.name || '',
        address: b.direccion || b.address || '',
        phone: b.telefono || b.phone || '57',
        email: b.email || '',
        negotiation_type: b.tipo_negociacion || b.negotiation_type || 'plaza',
        logo_url: b.url_logo || b.logo_url || '',
      });
      // Cargar sucursales y horarios en paralelo
      const [hRes, brRes] = await Promise.all([
        api.get(`/businesses/${user.business_id}/hours`),
        api.get('/branches').catch(() => ({ data: [] })),
      ]);
      setBranches(brRes.data.filter(br => br.activo));
      // Cargar horarios generales (sin sucursal)
      const existing = hRes.data;
      const dayMap = [1, 2, 3, 4, 5, 6, 0];
      const toHHMM = v => {
        if (!v) return null;
        const s = v instanceof Date
          ? `${String(v.getHours()).padStart(2,'0')}:${String(v.getMinutes()).padStart(2,'0')}`
          : v.toString().match(/\d{2}:\d{2}/)?.[0] || null;
        return s;
      };
      const built = DAYS.map((_, i) => {
        const d = dayMap[i];
        const found = existing.find(h => h.dia_semana === d);
        return found
          ? {
              day_of_week: d,
              open_time:  toHHMM(found.hora_apertura) || '08:00',
              close_time: toHHMM(found.hora_cierre)   || '18:00',
              is_open:    !!(found.abierto),
              has_break:   !!(found.tiene_break),
              break_start: toHHMM(found.hora_break_inicio) || '13:00',
              break_end:   toHHMM(found.hora_break_fin)    || '14:00',
            }
          : { day_of_week: d, open_time: '08:00', close_time: '18:00', is_open: false,
              has_break: false, break_start: '13:00', break_end: '14:00' };
      });
      setHours(built);
    } catch { setError('Error cargando configuración'); }
    finally { setLoading(false); }
  };

  const saveGeneral = async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      await api.put(`/businesses/${user.business_id}`, form);
      setSuccess('Configuración guardada');
      setTimeout(() => setSuccess(''), 3000);
      load();
    } catch (err) { setError(err.response?.data?.error || err.message || 'Error guardando configuración'); }
    finally { setSaving(false); }
  };

  const saveHours = async () => {
    // Validar los breaks antes de enviar: deben estar dentro del horario del día
    // y la hora de fin debe ser posterior a la de inicio.
    const invalido = hours.find(h =>
      h.is_open && h.has_break && (
        !h.break_start || !h.break_end ||
        h.break_start >= h.break_end ||
        h.break_start < h.open_time || h.break_end > h.close_time
      )
    );
    if (invalido) {
      const dia = DAYS[hours.indexOf(invalido)];
      setError(`El break de ${dia} debe estar dentro del horario de atención (${invalido.open_time} a ${invalido.close_time}) y la hora de fin debe ser posterior a la de inicio.`);
      setTimeout(() => setError(''), 6000);
      return;
    }

    setSavingHours(true); setSuccess(''); setError('');
    try {
      await api.put(`/businesses/${user.business_id}/hours`, {
        hours,
        branch_id: selectedBranchId || null,
      });
      setSuccess(selectedBranchId
        ? `Horarios de ${branches.find(b => String(b.id) === String(selectedBranchId))?.nombre || 'sucursal'} guardados`
        : 'Horarios generales guardados');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) { setError(err.response?.data?.error || 'Error guardando horarios'); }
    finally { setSavingHours(false); }
  };

  const toggleDay = idx => {
    const u = [...hours];
    u[idx] = { ...u[idx], is_open: !u[idx].is_open };
    setHours(u);
  };

  const updateHour = (idx, field, val) => {
    const u = [...hours];
    u[idx] = { ...u[idx], [field]: val };
    setHours(u);
  };

  const bookingUrl = business?.slug ? `${window.location.origin}/book/${business.slug}` : null;

  const copyLink = () => {
    if (!bookingUrl) return;
    navigator.clipboard.writeText(bookingUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(
      `Agenda tu cita en ${business?.nombre || ''} desde este link:\n${bookingUrl}`
    );
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (loading) return <div className="page-content"><p style={{ color: 'var(--color-text-muted)' }}>Cargando...</p></div>;

  const TABS = [
    { key: 'link', label: 'Mi Link' },
    { key: 'general', label: 'Datos Generales' },
    { key: 'horarios', label: 'Horarios' },
  ];

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mi Negocio</h1>
          <p className="page-subtitle">{business?.nombre || business?.name}</p>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
      {error && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div style={{ display: 'flex', gap: 6, marginBottom: 24, borderBottom: '1px solid var(--color-border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
              color: tab === t.key ? 'var(--color-gold)' : 'var(--color-text-muted)',
              borderBottom: tab === t.key ? '2px solid var(--color-gold)' : '2px solid transparent',
              fontFamily: 'var(--font-body)', fontWeight: tab === t.key ? 600 : 400,
              marginBottom: -1,
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ───── TAB: MI LINK ───── */}
      {tab === 'link' && (
        <div style={{ maxWidth: 560 }}>
          {!bookingUrl ? (
            <div className="alert alert-warning">
              Tu barbería no tiene un link configurado. Contacta al administrador.
            </div>
          ) : (
            <>
              {/* Card principal */}
              <div className="card" style={{
                border: '2px solid var(--color-gold)',
                background: 'linear-gradient(135deg, var(--color-surface-raised), var(--color-surface))',
                marginBottom: 20,
              }}>
                <div style={{ textAlign: 'center', padding: '12px 0 8px' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>🔗</div>
                  <h3 style={{ fontFamily: 'var(--font-display)', marginBottom: 4 }}>
                    Link de Agendamiento
                  </h3>
                  <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginBottom: 16 }}>
                    Comparte este link con tus clientes para que agenden su cita sin necesidad de llamar.
                  </p>

                  {/* URL visible */}
                  <div style={{
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 10,
                    padding: '12px 16px',
                    wordBreak: 'break-all',
                    fontSize: '0.9rem',
                    color: 'var(--color-gold)',
                    fontWeight: 600,
                    marginBottom: 16,
                    letterSpacing: '0.01em',
                  }}>
                    {bookingUrl}
                  </div>

                  <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                    <button className="btn btn-primary" onClick={copyLink} style={{ flex: 1 }}>
                      {copied ? '✓ Copiado!' : '📋 Copiar Link'}
                    </button>
                    <button className="btn btn-secondary" onClick={shareWhatsApp} style={{ flex: 1 }}>
                      Compartir por WhatsApp
                    </button>
                  </div>
                </div>
              </div>

              {/* Info de uso */}
              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-header">
                  <h3 className="card-title" style={{ fontSize: '0.9rem' }}>¿Qué pueden hacer tus clientes?</h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 0' }}>
                  {[
                    ['✂️', 'Seleccionar el servicio y barbero de su preferencia'],
                    ['📅', 'Ver el calendario de disponibilidad en tiempo real'],
                    ['✅', 'Confirmar su cita con nombre y celular'],
                    ['📋', 'Consultar el historial de sus citas anteriores'],
                    ['❌', 'Cancelar o reagendar su cita sin necesidad de llamar'],
                  ].map(([icon, txt], i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, fontSize: '0.85rem' }}>
                      <span style={{ fontSize: '1.1rem', minWidth: 24, textAlign: 'center' }}>{icon}</span>
                      <span style={{ color: 'var(--color-text-muted)' }}>{txt}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Identificador del slug */}
              <div style={{ fontSize: '0.78rem', color: 'var(--color-text-faint)', textAlign: 'center' }}>
                Identificador: <code style={{ color: 'var(--color-text-muted)' }}>{business.slug}</code>
              </div>
            </>
          )}
        </div>
      )}

      {/* ───── TAB: DATOS GENERALES ───── */}
      {tab === 'general' && (
        <div className="card" style={{ padding: '1.5rem', maxWidth: 600 }}>
          <div className="form-group">
            <label className="form-label">Nombre del Negocio *</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Dirección</label>
            <input className="form-input" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label"># de celular con WhatsApp</label>
            <input
              className="form-input"
              value={form.phone}
              placeholder="573001234567"
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, '');
                setForm({ ...form, phone: digits.startsWith('57') ? digits : '57' + digits });
              }}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
              Debe ser el número de WhatsApp del negocio, desde el cual salen los mensajes de confirmación. Siempre inicia con 57 (Colombia).
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Correo</label>
            <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Método de Negociación</label>
            <select className="form-input" value={form.negotiation_type} onChange={e => setForm({ ...form, negotiation_type: e.target.value })}>
              <option value="plaza">Por Plaza (arriendo de silla)</option>
              <option value="porcentaje">Por Porcentaje de ventas</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Logo del Negocio</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              {form.logo_url && (
                <img src={form.logo_url} alt="Logo" style={{ height: 72, width: 72, objectFit: 'contain', borderRadius: 10, border: '1px solid var(--color-border)', background: 'var(--color-surface-raised)' }} />
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{
                  display: 'inline-block', padding: '8px 16px', background: 'var(--color-surface-raised)',
                  border: '1px solid var(--color-border)', borderRadius: 8, cursor: 'pointer',
                  fontSize: '0.85rem', color: 'var(--color-text)', fontFamily: 'var(--font-body)',
                }}>
                  {form.logo_url ? 'Cambiar imagen' : 'Subir imagen'}
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async e => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (!file) return;
                      try {
                        // Se comprime antes de guardar: el logo viaja dentro del
                        // registro del negocio y una foto de celular sin reducir
                        // pesa varios MB.
                        const dataUrl = await fileToCompressedDataUrl(file, 400, 0.85);
                        setForm(f => ({ ...f, logo_url: dataUrl }));
                      } catch (err) {
                        alert(err.message || 'No se pudo procesar la imagen');
                      }
                    }}
                  />
                </label>
                {form.logo_url && (
                  <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: '0.8rem', textAlign: 'left', padding: 0 }}
                    onClick={() => setForm(f => ({ ...f, logo_url: '' }))}>
                    Quitar logo
                  </button>
                )}
              </div>
            </div>
          </div>
          <div style={{ marginTop: '1.5rem' }}>
            <button className="btn btn-primary" onClick={saveGeneral} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}

      {/* ───── TAB: HORARIOS ───── */}
      {/* Sub-pestañas de Horarios: semanales | excepciones del calendario */}
      {tab === 'horarios' && (
        <div style={{ display: 'flex', gap: 6, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
          {[
            ['semanales',   '📅 Horarios semanales', false],
            ['excepciones', '🗓️ Excepciones del calendario', true],
          ].map(([k, etiqueta, esNuevo]) => (
            <button
              key={k}
              onClick={() => setSubTab(k)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 18px', borderRadius: 10, cursor: 'pointer',
                border: `1px solid ${subTab === k ? 'var(--color-gold)' : 'var(--color-border)'}`,
                background: subTab === k ? 'rgba(201,168,76,0.08)' : 'var(--color-surface)',
                color: subTab === k ? 'var(--color-gold)' : 'var(--color-text-muted)',
                fontWeight: subTab === k ? 700 : 500, fontSize: '0.88rem',
                fontFamily: 'var(--font-body)',
              }}
            >
              {etiqueta}
              {esNuevo && (
                <span style={{
                  fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em',
                  background: '#7c3aed', color: '#fff', padding: '2px 6px', borderRadius: 99,
                }}>NUEVO</span>
              )}
            </button>
          ))}
        </div>
      )}

      {tab === 'horarios' && subTab === 'excepciones' && (
        <CalendarExceptions businessId={user.business_id} branchId={selectedBranchId || null} />
      )}

      {tab === 'horarios' && subTab === 'semanales' && (
        <div className="card" style={{ padding: '1.5rem', maxWidth: 660 }}>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
            Define los días y horarios de atención de tu barbería.
          </p>

          {/* Selector de sucursal — solo cuando hay sucursales activas */}
          {branches.length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="form-label">Configurar horarios de:</label>
              <select
                className="form-select"
                value={selectedBranchId}
                onChange={e => {
                  setSelectedBranchId(e.target.value);
                  loadHours(e.target.value || null);
                }}
              >
                <option value="">🏢 Negocio general (todas las sucursales)</option>
                {branches.map(br => (
                  <option key={br.id} value={String(br.id)}>🏪 {br.nombre}</option>
                ))}
              </select>
              {selectedBranchId && (
                <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 6 }}>
                  Los horarios de una sucursal sobreescriben los generales para ese local.
                  Si una sucursal no tiene horarios propios, se usan los del negocio.
                </p>
              )}
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {hours.map((h, i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '120px 1fr 1fr 80px',
                alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem',
                background: h.is_open ? 'rgba(201,168,76,0.06)' : 'var(--color-surface)',
                borderRadius: 8,
                border: `1px solid ${h.is_open ? 'var(--color-gold)' : 'var(--color-border)'}`,
                opacity: h.is_open ? 1 : 0.6,
              }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontWeight: 600 }}>
                  <input type="checkbox" checked={!!h.is_open} onChange={() => toggleDay(i)} />
                  {DAYS[i]}
                </label>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 2 }}>Apertura</div>
                  <input type="time" className="form-input" style={{ padding: '6px 8px', fontSize: '0.85rem' }}
                    value={h.open_time} disabled={!h.is_open} onChange={e => updateHour(i, 'open_time', e.target.value)} />
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 2 }}>Cierre</div>
                  <input type="time" className="form-input" style={{ padding: '6px 8px', fontSize: '0.85rem' }}
                    value={h.close_time} disabled={!h.is_open} onChange={e => updateHour(i, 'close_time', e.target.value)} />
                </div>
                <div style={{ textAlign: 'center', fontSize: '0.8rem', color: h.is_open ? 'var(--color-gold)' : 'var(--color-text-muted)' }}>
                  {h.is_open ? 'Abierto' : 'Cerrado'}
                </div>

                {/* Break / descanso del día — ocupa toda la fila siguiente */}
                <div style={{
                  gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '120px 1fr 1fr 80px',
                  alignItems: 'center', gap: '1rem',
                  borderTop: '1px dashed var(--color-border)', paddingTop: 8, marginTop: 2,
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: h.is_open ? 'pointer' : 'not-allowed', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                    <input
                      type="checkbox"
                      checked={!!h.has_break}
                      disabled={!h.is_open}
                      onChange={e => updateHour(i, 'has_break', e.target.checked)}
                    />
                    ☕ Break
                  </label>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 2 }}>Inicio break</div>
                    <input type="time" className="form-input" style={{ padding: '6px 8px', fontSize: '0.85rem' }}
                      value={h.break_start || '13:00'} disabled={!h.is_open || !h.has_break}
                      onChange={e => updateHour(i, 'break_start', e.target.value)} />
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', marginBottom: 2 }}>Fin break</div>
                    <input type="time" className="form-input" style={{ padding: '6px 8px', fontSize: '0.85rem' }}
                      value={h.break_end || '14:00'} disabled={!h.is_open || !h.has_break}
                      onChange={e => updateHour(i, 'break_end', e.target.value)} />
                  </div>
                  <div style={{ textAlign: 'center', fontSize: '0.75rem', color: h.is_open && h.has_break ? 'var(--color-gold)' : 'var(--color-text-muted)' }}>
                    {h.is_open && h.has_break ? 'Con break' : 'Sin break'}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '1.5rem' }}>
            <button className="btn btn-primary" onClick={saveHours} disabled={savingHours}>
              {savingHours ? 'Guardando...' : 'Guardar Horarios'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
